import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import { capitalizeFirstLetter, formatCurrency, formatDate } from 'utility/functions';
import {
  GetUserSubscriptionsByIdResult,
  GetUserSubscriptionTransactionsByIdResult,
} from 'utility/types/dataProvider';
import { isNumber } from 'remeda';

dayjs.extend(relativeTime);
dayjs.extend(duration);

type GatewayTransactionFields = {
  gateway_transaction_id?: string | number | null;
};

export type TransactionItem = Extract<
  GetUserSubscriptionTransactionsByIdResult['data']['items'],
  unknown[]
>[number] &
  GatewayTransactionFields;

export type SubscriptionSearchItem = Extract<
  GetUserSubscriptionsByIdResult['data']['items'],
  unknown[]
>[number];

export type SubscriptionSearchItemStatus = SubscriptionSearchItem['status'];

interface FormatTransactionItemParams {
  transaction: TransactionItem;
  timeZone?: string;
}
export function formatTransactionItem({
  transaction,
  timeZone,
}: FormatTransactionItemParams) {
  // Normalize amounts and ensure they are valid numbers or fallback to 0
  const rateAmount = transaction?.rate?.amount;
  const taxAmount = transaction?.tax?.amount;
  const rateCurrency = transaction?.rate?.currency;

  // Api may send a num or a string so normalize and fallback to 0
  const validRateAmount = isNumber(rateAmount)
    ? rateAmount
    : parseInt(String(rateAmount), 10) || 0;
  const validTaxAmount = isNumber(taxAmount)
    ? taxAmount
    : parseInt(String(taxAmount), 10) || 0;

  // combine and truncate to cents
  const totalFixed = (validRateAmount + validTaxAmount).toFixed(0);

  // get total with tax for UI display formatted
  const totalWithTax = formatCurrency(totalFixed, rateCurrency);

  const normalizedGatewayTransactionId =
    transaction?.gateway_transaction_id !== undefined &&
    transaction?.gateway_transaction_id !== null
      ? String(transaction.gateway_transaction_id)
      : undefined;

  return {
    fullItem: transaction,
    id: transaction?.id,
    gateway_transaction_id: normalizedGatewayTransactionId,
    rate_id: transaction?.rate?.id,
    rate_currency: transaction?.rate?.currency,
    tax_rate: transaction?.tax?.rate,
    tax_region: transaction?.tax?.region,
    pm_id: transaction?.payment_method?.id,
    pm_type: transaction?.payment_method?.type,
    pm_card_type: transaction?.payment_method?.card_type,
    pm_last4: transaction?.payment_method?.last4,
    pm_expiration: transaction?.payment_method?.expiration,
    pm_name: transaction?.payment_method?.name,
    status: transaction?.status,
    address_line1: transaction?.billing_address?.line1,
    address_line2: transaction?.billing_address?.line2,
    address_municipality: transaction?.billing_address?.municipality,
    address_region: transaction?.billing_address?.region,
    address_postal_code: transaction?.billing_address?.postal_code,
    address_country: transaction?.billing_address?.country,
    refund_transaction_id: transaction?.refund_transaction_id, // If exists, this transaction was refunded
    // --- formatted & convenience items ---
    rate_duration: transaction?.rate?.duration
      ? formatSubscriptionDuration(transaction?.rate?.duration)
      : undefined,
    rate_amount: transaction?.rate?.amount
      ? formatCurrency(transaction?.rate?.amount, transaction?.rate?.currency)
      : undefined,
    tax_amount: transaction?.tax?.amount
      ? formatCurrency(transaction?.tax?.amount, transaction?.rate?.currency)
      : undefined,
    start_time: formatDate({
      date: transaction?.start_time,
      shiftTz: timeZone,
    }),
    expire_time: formatDate({
      date: transaction?.expire_time,
      shiftTz: timeZone,
    }),
    end_time: formatDate({
      date: transaction?.end_time,
      shiftTz: timeZone,
    }),
    // Start time formatted for display
    trx_time_long: formatDate({
      date: transaction?.transaction_time,
      shiftTz: timeZone,
      format: {
        dateStyle: 'medium',
        timeStyle: 'short',
      },
    }),
    // Status text resolved to human-readable string
    status_text: capitalizeFirstLetter(transaction?.status || 'unknown'),
    // Total with tax
    total_with_tax: totalWithTax,
  };
}

// "active" and "grace" have not ended, the rest are considered to have ended.
export const checkSubHasEnded = (status: string | null | undefined) => {
  const statusLowercase = status?.toLowerCase();
  return Boolean(
    [
      'expired',
      'former',
      'cancel',
      'cancelled',
      'canceled',
      'active and canceled',
    ].includes(statusLowercase || ''),
  );
};

interface FormatSubscriptionSearchItemDataParams {
  item: SubscriptionSearchItem;
  timeZone?: string;
}

export function formatSubscriptionSearchItemData({
  item,
  timeZone,
}: FormatSubscriptionSearchItemDataParams) {
  const currentUtc = dayjs.utc();
  const expireTimeValid = item?.expire_time && dayjs(item?.expire_time).isValid();
  const expiryTxt = expireTimeValid
    ? dayjs.utc(item?.expire_time).isAfter(currentUtc)
      ? 'expires'
      : 'expired'
    : undefined;

  return {
    fullItem: item,
    subscriptionId: item?.subscription_id,
    status: item?.status,
    recurring: item?.recurring,
    startTime: item?.start_time,
    expireTime: item?.expire_time,
    startTimeFmt: formatDate({
      date: item?.start_time,
      shiftTz: timeZone,
    }),
    expireTimeFmt: formatDate({
      date: item?.expire_time,
      shiftTz: timeZone,
    }),
    serviceName: item?.service?.name,
    serviceTitle: item?.service?.title,
    serviceId: item?.service?.service_id,
    serviceType: item?.service?.type,
    hasRateInfo: Boolean(item?.service?.rate),
    rateAmount: item?.service?.rate?.amount,
    rateCurrency: item?.service?.rate?.currency,
    rateDuration: item?.service?.rate?.duration,
    rateName: item?.service?.rate?.name,
    rateId: item?.service?.rate?.id,
    rateAmountFmt: item?.service?.rate?.amount
      ? formatCurrency(item?.service?.rate?.amount, item?.service?.rate?.currency)
      : undefined,
    rateDurationFmt: item?.service?.rate?.duration
      ? formatSubscriptionDuration(
        item?.service?.rate?.duration,
        Boolean(item?.recurring),
      )
      : undefined,

    // Some of these fields may or may not be present based on the subscription - SUBSCRIPTN-557511, SUBSCRIPTN-557557
    renewalTimeFmt: formatDate({
      date: item?.renewal_time,
      shiftTz: timeZone,
    }),
    cancelTimeFmt: formatDate({
      date: item?.cancel_time,
      shiftTz: timeZone,
    }),
    hasTrsRateInfo: Boolean(item?.service?.transition_rate),
    trsRateAmount: item?.service?.transition_rate?.amount,
    trsRateCurrency: item?.service?.transition_rate?.currency,
    trsRateDuration: item?.service?.transition_rate?.duration,
    trsRateName: item?.service?.transition_rate?.name,
    trsRateId: item?.service?.transition_rate?.id,
    trsRateAmountFmt: item?.service?.transition_rate?.amount
      ? formatCurrency(
        item?.service?.transition_rate?.amount,
        item?.service?.transition_rate?.currency,
      )
      : undefined,
    trsRateDurationFmt: item?.service?.transition_rate?.duration
      ? formatSubscriptionDuration(item?.service?.transition_rate?.duration)
      : undefined,
    // Utilities for UI
    isThirdParty: item?.service?.type !== 'subscription', // some are 1p e.g 'free', but we display like 3p
    hasEnded: checkSubHasEnded(item?.status),
    paidTense: Boolean(item?.status !== 'active') ? 'paid' : 'paying',
    recurringTense: item?.recurring ? 'every' : 'for',
    expireTense: expiryTxt,
  };
}

/**
 * @param duration Pretty-dates formatter for Subscription Dates
 * @param omitNumberIfSingular Optional boolean to omit the number for singular durations like "1 month" to just "month"
 * @returns string
 * @see https://day.js.org/docs/en/durations/humanize for another alternative
 */
export const formatSubscriptionDuration = (
  duration: string | number | undefined | null,
  omitNumberIfSingular?: boolean,
): string => {
  if (!duration) return '';

  const days = Number(duration);

  // Helper function used only for cases where omitNumberIfSingular is relevant
  const getSingularDurationString = (singular: string): string => {
    return omitNumberIfSingular ? singular : `1 ${singular}`;
  };

  if (days === 7) return getSingularDurationString('week');
  if (days === 28) return '28 days'; // Always return "28 days" for exactly 28 days
  if (days >= 29 && days <= 31) return getSingularDurationString('month');
  if (days === 365 || days === 366) return getSingularDurationString('year');

  // For cases where the duration is not singular or doesn't have a special rule
  if (days === 90) return '3 months';
  if (days === 180) return '6 months';
  if (days % 7 === 0) return `${days / 7} weeks`;
  if (days % 30 === 0) return `${days / 30} months`;

  // Default to X days for other day counts
  return `${days} days`;
}; // Formatted Subscription Item for easier use in the UI

// Helper for calculateSavings - estimate billing cycles per year based on durations
export function getBillingCycleEstimate(duration: number): number {
  if (!duration || duration <= 0) return 0;

  if (duration === 1) return 365; // Daily
  if (duration === 7) return 52; // Weekly
  if (duration === 14) return 26; // Bi-weekly
  if (duration >= 29 && duration <= 31) return 12; // Monthly
  if (duration === 90) return 4; // Quarterly
  if (duration === 180) return 2; // Semi-annually
  if (duration === 365 || duration === 366) return 1; // Yearly or leap year

  // Handle multiples of weeks
  if (duration % 7 === 0) {
    const weeks = duration / 7;
    return Math.max(Math.floor(52 / weeks), 1);
  }

  // Handle multiples of months (~30 days)
  if (duration % 30 === 0) {
    const months = duration / 30;
    return Math.max(Math.floor(12 / months), 1);
  }

  // For irregular other durations, return a fractional multiplier to get to ~365 days
  return 365 / duration;
}

interface CalculateSavingsResult {
  savings: 'less' | 'more' | 'equal';
  daily: string;
  monthly: string;
  yearly: string;
  percentage: string;
}

export function calculateSavings(
  currentRatePrice: number,
  currentRateDuration: number,
  comparedRatePrice: number,
  comparedRateDuration: number,
  currency?: string,
): CalculateSavingsResult | undefined {
  if (!currentRateDuration || !comparedRateDuration) {
    return undefined;
  }

  const billingCyclesCurrent = getBillingCycleEstimate(currentRateDuration);
  const billingCyclesCompared = getBillingCycleEstimate(comparedRateDuration);

  // Calculate total costs for each plan over one year
  const totalCostCurrent = currentRatePrice * billingCyclesCurrent;
  const totalCostCompared = comparedRatePrice * billingCyclesCompared;

  // Round total costs to mitigate some float precision issues
  const roundedTotalCostCurrent = Math.round(totalCostCurrent);
  const roundedTotalCostCompared = Math.round(totalCostCompared);

  // Calculate total savings
  const totalSavings = roundedTotalCostCurrent - roundedTotalCostCompared;

  // Determine if the new plan is cheaper, more expensive, or equal
  const savings = totalSavings > 0 ? 'less' : totalSavings < 0 ? 'more' : 'equal';

  // Calculate percentage difference
  const percentageDifference =
    roundedTotalCostCurrent !== 0
      ? ((roundedTotalCostCompared - roundedTotalCostCurrent) / roundedTotalCostCurrent) *
      100
      : 0;

  // Calculate daily savings based on 365 days
  const dailySavings = totalSavings / 365;

  // Calculate monthly savings based on 12 months
  const monthlySavings = totalSavings / 12;

  // Convert cents to dollars and format
  const dailyAmount = formatCurrency(Math.abs(dailySavings), currency) || '';
  const monthlyAmount = formatCurrency(Math.abs(monthlySavings), currency) || '';
  const yearlyAmount = formatCurrency(Math.abs(totalSavings), currency) || '';
  const percentage = Math.abs(percentageDifference).toFixed(2);

  return {
    savings: savings,
    daily: dailyAmount,
    monthly: monthlyAmount,
    yearly: yearlyAmount,
    percentage: percentage,
  };
}
